"use client";

import { ExternalLink } from "lucide-react";
import type { SeeAlsoComponent } from "../../types";
import { GuardedLink } from "../../runtime/DevPlaceholder";
import { useLocale } from "../../runtime/RendererRuntime";
import { resolveLinkHref } from "../../utils/links";

/**
 * Panneau « Voir aussi » : un sur-titre orange, un titre, un chapô court et
 * deux à quatre destinations liées.
 *
 * Il existe ici, dans le SDK, parce que les DEUX familles de pages en ont
 * besoin : les pages-sujets macroéconomiques (rendues par le Next.js) et les
 * pages CMS de la section Marché des changes. Un seul composant, donc une
 * seule apparence — c'est ce qui empêche les deux de diverger, comme cela
 * s'était produit pour l'en-tête de sujet.
 */
export function SeeAlsoRenderer({ component }: { component: SeeAlsoComponent }) {
  const locale = useLocale();
  const english = locale.toLowerCase().startsWith("en");
  const { eyebrow, title, text, items } = component.content;
  const links = (items ?? []).filter((item) => item?.title);
  if (links.length === 0) return null;

  return (
    <section className="rounded-md border border-bcc-panel shadow-bcc-panel bg-white p-6 md:p-7">
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#E98A00]">
        {eyebrow || (english ? "Live source" : "Source en direct")}
      </p>
      <h2 className="mt-1 text-xl font-bold text-[#005CA9]" style={{ fontFamily: "var(--font-aleo)" }}>
        {title || (english ? "See also" : "Voir aussi")}
      </h2>
      {text ? <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">{text}</p> : null}
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {links.map((item, index) => (
          <GuardedLink
            key={`${resolveLinkHref(item.href) ?? ""}-${index}`}
            href={item.href}
            label={item.title}
            block
            fallback="unwrap"
            className="group flex items-start gap-3 rounded-md border border-[#E5EDF2] p-4 transition hover:border-[#8DBFDF]"
          >
            <ExternalLink className="mt-0.5 h-[18px] w-[18px] shrink-0 text-[#0069B4]" aria-hidden="true" />
            <span className="min-w-0 flex-1">
              <span className="block font-bold leading-5 text-[#005CA9] group-hover:underline">{item.title}</span>
              {item.text ? <span className="mt-1 block text-sm leading-5 text-slate-600">{item.text}</span> : null}
            </span>
          </GuardedLink>
        ))}
      </div>
    </section>
  );
}
